'use client';

import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import MarketingReveal from './MarketingReveal';
import { scrollMarketingSection } from './marketing-scroll';
import { MARKETING, MARKETING_NUM_SX } from './marketing-tokens';

/** Sized parent for pair images (next/image fill or legacy Box img). */
export const MARKETING_PAIR_IMAGE_SX = {
  position: 'relative',
  width: '100%',
  height: { xs: 240, sm: 300, md: 360 },
  objectFit: 'cover',
  display: 'block',
  boxShadow: '0 32px 64px rgba(0, 0, 0, 0.5)',
};

export const MARKETING_PAIR_BLOCK_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  gap: { xs: '28px', md: '40px 48px' },
  alignItems: 'start',
};

export function MarketingIndexLink({ section, active }) {
  return (
    <Box
      component="a"
      href={`#${section.id}`}
      onClick={event => {
        event.preventDefault();
        scrollMarketingSection(section.id);
      }}
      sx={{
        display: 'flex',
        gap: '14px',
        alignItems: 'baseline',
        fontFamily: MARKETING.sans,
        fontWeight: 400,
        fontSize: '12px',
        letterSpacing: '0.06em',
        color: active ? MARKETING.text : MARKETING.indexIdle,
        textDecoration: 'none',
        transition: 'color 0.2s ease',
        '&:hover': {
          color: active ? MARKETING.text : 'rgba(239, 232, 220, 0.78)',
        },
      }}
    >
      <Box
        component="span"
        sx={{
          color: active ? MARKETING.accent : 'inherit',
          minWidth: '1.4em',
          transition: 'color 0.2s ease',
        }}
      >
        {section.num}
      </Box>
      <span>{section.label}</span>
    </Box>
  );
}

MarketingIndexLink.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    num: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  active: PropTypes.bool,
};

export function MarketingNumberedBlock({ id, num, children, sx, contentSx, stagger = false }) {
  return (
    <MarketingReveal
      component="section"
      id={id}
      animate={!stagger}
      sx={{
        px: MARKETING.padX,
        py: '72px',
        borderBottom: `1px solid ${MARKETING.hairline}`,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '88px 1fr' },
        gap: '32px',
        alignItems: 'start',
        scrollMarginTop: '24px',
        transition: `opacity 0.8s ${MARKETING.ease}, transform 0.8s ${MARKETING.ease}`,
        ...sx,
      }}
    >
      <Typography sx={{ ...MARKETING_NUM_SX, display: { xs: 'none', sm: 'block' } }}>
        {num}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', ...contentSx }}>
        {children}
      </Box>
    </MarketingReveal>
  );
}

MarketingNumberedBlock.propTypes = {
  id: PropTypes.string.isRequired,
  num: PropTypes.string.isRequired,
  children: PropTypes.node,
  sx: PropTypes.object,
  contentSx: PropTypes.object,
  stagger: PropTypes.bool,
};

export function MarketingStickyIndex({ sections, activeId }) {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' },
        alignSelf: 'stretch',
        bgcolor: MARKETING.canvas,
        borderRight: `1px solid ${MARKETING.hairline}`,
      }}
    >
      <Box
        component="nav"
        aria-label="On this page"
        sx={{
          position: 'sticky',
          top: 0,
          py: '64px',
          pl: MARKETING.padX,
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        <Typography
          sx={{
            fontFamily: MARKETING.sans,
            fontWeight: 400,
            fontSize: '10px',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'rgba(239, 232, 220, 0.4)',
            mb: '6px',
          }}
        >
          Index
        </Typography>
        {sections.map(section => (
          <MarketingIndexLink key={section.id} section={section} active={activeId === section.id} />
        ))}
      </Box>
    </Box>
  );
}

MarketingStickyIndex.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      num: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  activeId: PropTypes.string,
};
